/**
 * Wraps a store-provided callback so errors are caught, logged to Loki, and
 * don't propagate to callers (which can corrupt UI interactions or React state).
 *
 * Analogous to safeTemplateCall (which wraps template.call()), but for
 * imperative callbacks supplied from store configs — e.g. updateCallback,
 * onVehicleSelected, onVehicleDiscarded.
 *
 * Uses dynamic import() for diagnostics.js so this file is safe to import
 * from anywhere, including prelude modules. A static import would transitively
 * load componentlessWidgetsConfig → widgetProcessor at module init time and
 * break the InitFunc guard. See CLIENT_DIAGNOSTICS.md § "Do NOT statically
 * import diagnostics.js from prelude modules".
 *
 * @param fn     The callback to wrap. null / undefined is treated as a no-op.
 * @param source Identifier forwarded to logRuntimeError as the 'scenario'
 *               label in Grafana (e.g. 'update_callback', 'on_vehicle_selected').
 * @param extra  Optional key/value pairs appended to the Loki log entry.
 */
function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    value !== null &&
    (typeof value === 'object' || typeof value === 'function') &&
    typeof (value as { then?: unknown }).then === 'function'
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- generic constraint intentionally uses any
export function safeCallback<T extends (...args: any[]) => any>(
  fn: T | null | undefined,
  source: string,
  extra?: Record<string, string>,
): (...args: Parameters<T>) => ReturnType<T> | undefined {
  return (...args: Parameters<T>): ReturnType<T> | undefined => {
    if (!fn) {
      return undefined;
    }
    try {
      const result = fn(...args) as ReturnType<T>;
      if (isThenable(result)) {
        Promise.resolve(result).catch((err) => {
          console.error(`[cm-diag] ${source} rejected`, err);
          import('Diagnostics/diagnostics')
            .then(({ logRuntimeError }) => logRuntimeError({ source: `${source}_async`, error: err, extra }))
            .catch(() => {});
        });
      }
      return result;
    } catch (err) {
      console.error(`[cm-diag] ${source} threw`, err);
      import('Diagnostics/diagnostics')
        .then(({ logRuntimeError }) => logRuntimeError({ source, error: err, extra }))
        .catch(() => {});
      return undefined;
    }
  };
}
